<template>
    <div class="pnr-app">

        <!-- ============ VÉRIFICATION D'ACCÈS ============ -->
        <div v-if="bootState === 'checking'" class="pnr-boot">
            <div class="pnr-boot-logo"><i class="ri-shopping-basket-2-line"></i></div>
            <div class="pnr-boot-spin"><i class="ri-loader-4-line pnr-spin"></i></div>
            <p class="pnr-boot-txt" v-if="seedProgress">
                Chargement du catalogue…<br />
                <b>{{ seedProgress.fait }} / {{ seedProgress.total }}</b> articles
            </p>
            <p class="pnr-boot-txt" v-else>Vérification de votre accès…</p>
        </div>

        <!-- ============ VUE SCAN ============ -->
        <template v-else-if="view === 'scan'">

            <!-- Header -->
            <div class="pnr-header">
                <div class="pnr-brand">
                    <i class="ri-shopping-basket-2-line"></i>
                    <span>Mon panier</span>
                </div>
                <div class="pnr-head-right">
                    <span class="pnr-net" :class="online ? 'on' : 'off'">
                        <i :class="online ? 'ri-wifi-line' : 'ri-wifi-off-line'"></i>
                        {{ online ? 'en ligne' : 'hors ligne' }}
                    </span>
                    <button class="pnr-imgbtn" :class="{ on: avecImages }"
                            :title="avecImages ? 'Masquer les images' : 'Afficher les images'"
                            :aria-pressed="avecImages ? 'true' : 'false'" @click="basculerImages">
                        <i :class="avecImages ? 'ri-image-line' : 'ri-image-off-line'"></i>
                    </button>
                    <button class="pnr-syncbtn" :class="refreshState" :disabled="refreshState === 'loading'"
                            :title="syncTitle" @click="rafraichirCatalogue">
                        <i :class="syncIcon"></i>
                    </button>
                    <button class="pnr-newbtn" title="Nouveau panier" @click="nouveauPanier">
                        <i class="ri-add-circle-line"></i>
                    </button>
                </div>
            </div>

            <!-- Catalogue servi depuis le cache : les prix peuvent avoir bougé -->
            <div v-if="bootState === 'offline'" class="pnr-offline">
                <i class="ri-wifi-off-line"></i>
                <span>Hors ligne - catalogue du {{ dateCatalogue }}. Les prix peuvent avoir changé.</span>
            </div>

            <!-- Zone caméra -->
            <div class="pnr-scanzone">
                <video id="pnr-video" class="pnr-reader" muted playsinline autoplay></video>
                <div class="pnr-scanframe" v-if="!scanError"></div>
                <button v-if="torchAvailable" class="pnr-torch" :class="{ on: torchOn }" @click="toggleTorch">
                    <i class="ri-flashlight-line"></i>
                </button>
                <div v-if="scanError" class="pnr-scanerr">
                    <i class="ri-camera-off-line"></i>
                    <span>Caméra indisponible - utilisez la saisie manuelle ci-dessous.</span>
                </div>
                <div class="pnr-scanhint" v-else>Visez le code-barres du produit</div>
            </div>

            <!-- Mode de scan -->
            <div class="pnr-modebar" v-if="!scanError">
                <div class="pnr-seg">
                    <button :class="{ active: scanMode === 'bouton' }" @click="setMode('bouton')">
                        <i class="ri-focus-3-line"></i> Au bouton
                    </button>
                    <button :class="{ active: scanMode === 'continu' }" @click="setMode('continu')">
                        <i class="ri-flashlight-line"></i> Continu
                    </button>
                </div>
            </div>

            <!-- Action de scan -->
            <button v-if="scanMode === 'bouton' && !scanError" class="pnr-shutter"
                    :class="{ busy: captureActive }" @click="armCapture">
                <i :class="captureActive ? 'ri-loader-4-line pnr-spin' : 'ri-camera-line'"></i>
                {{ captureActive ? 'Recherche…' : 'Scanner un produit' }}
            </button>
            <div v-else-if="scanMode === 'continu' && !scanError" class="pnr-continu-hint">
                <i class="ri-loader-4-line pnr-spin"></i> Scan continu actif - visez les produits
            </div>

            <!-- Saisie manuelle (test / secours) -->
            <div class="pnr-manual">
                <input v-model="manualCode" @keyup.enter="ajouterManuel" type="text" inputmode="numeric"
                       placeholder="Saisir un code-barres…" class="pnr-input" />
                <button class="pnr-addbtn" @click="ajouterManuel">Ajouter</button>
            </div>

            <!-- Feedback -->
            <transition name="pnr-fade">
                <div v-if="feedback" class="pnr-feedback" :class="'fb-' + feedback.type">
                    <i :class="feedbackIcon"></i>
                    <div class="pnr-fb-text">
                        <div class="pnr-fb-title">{{ feedback.title }}</div>
                        <div class="pnr-fb-sub" v-if="feedback.sub">{{ feedback.sub }}</div>
                    </div>
                    <button v-if="feedback.undoable" class="pnr-fb-undo" @click="undoLast">
                        <i class="ri-arrow-go-back-line"></i> Annuler
                    </button>
                </div>
            </transition>

            <!-- Liste panier -->
            <div class="pnr-list">
                <div v-if="items.length === 0" class="pnr-empty">
                    <i class="ri-basket-line"></i>
                    <p>Votre panier est vide.<br>Scannez votre premier produit.</p>
                </div>
                <div v-for="line in items" :key="line.ean" class="pnr-line">
                    <img v-if="photoArticle(line)" :src="photoArticle(line)" alt="" class="pnr-line-img"
                         loading="lazy" decoding="async" @error="photoIllisible(line)" />
                    <div class="pnr-line-info">
                        <div class="pnr-line-name">{{ line.libelle }}</div>
                        <div class="pnr-line-pu">{{ fmt(line.prix) }} / unité</div>
                        <div v-if="line.emplacement" class="pnr-line-emp">
                            <i class="ri-map-pin-line"></i>
                            <span v-if="line.emplacement.rayon">Rayon {{ line.emplacement.rayon }}</span>
                            <span v-if="line.emplacement.couloir">· Couloir {{ line.emplacement.couloir }}</span>
                            <span v-if="line.emplacement.etagere">· {{ line.emplacement.etagere }}</span>
                        </div>
                    </div>
                    <div class="pnr-stepper">
                        <button @click="decr(line)"><i class="ri-subtract-line"></i></button>
                        <span class="pnr-qte" @click="openKeypad(line)">{{ line.qte }}</span>
                        <button @click="incr(line)"><i class="ri-add-line"></i></button>
                    </div>
                    <div class="pnr-line-right">
                        <div class="pnr-line-total">{{ fmt(line.prix * line.qte) }}</div>
                        <button class="pnr-del" @click="removeLine(line)"><i class="ri-delete-bin-line"></i></button>
                    </div>
                </div>
            </div>

            <!-- Barre total + action -->
            <div class="pnr-bottom">
                <div class="pnr-total">
                    <span>Total indicatif</span>
                    <strong>{{ fmt(total) }}</strong>
                </div>
                <button class="pnr-cta" :disabled="items.length === 0" @click="genererCodeCaisse">
                    <i class="ri-qr-code-line"></i> Générer le code caisse
                </button>
                <div class="pnr-session">{{ items.length }} article(s) · session {{ sessionId }}</div>
            </div>

            <!-- FAB recherche -->
            <button v-if="!keypad.open && !showSearch" class="pnr-fab" @click="toggleSearch" title="Rechercher un produit">
                <i class="ri-search-line"></i>
            </button>

            <!-- Modal recherche -->
            <div v-if="showSearch" class="pnr-modal" @click.self="closeSearch">
                <div class="pnr-search-modal">
                    <div class="pnr-search-head">
                        <i class="ri-search-line"></i>
                        <input ref="searchInput" v-model="searchQuery" @input="onSearchInput" type="text"
                               placeholder="Rechercher un produit…" class="pnr-search-input" />
                        <button v-if="searchQuery" class="pnr-search-clear" @click="searchQuery = ''; searchResults = []">
                            <i class="ri-close-line"></i>
                        </button>
                    </div>
                    <div v-if="searching" class="pnr-search-status">
                        <i class="ri-loader-4-line pnr-spin"></i> Recherche…
                    </div>
                    <div v-else-if="searchQuery && searchResults.length === 0" class="pnr-search-status">
                        <i class="ri-inbox-2-line"></i> Aucun résultat
                    </div>
                    <div v-else class="pnr-search-results">
                        <div v-for="r in searchResults" :key="r.ean" class="pnr-search-item" @click="ajouterParRecherche(r)">
                            <img v-if="photoArticle(r)" :src="photoArticle(r)" alt="" class="pnr-line-img"
                                 loading="lazy" decoding="async" @error="photoIllisible(r)" />
                            <div class="pnr-search-item-info">
                                <div class="pnr-search-item-name">{{ r.libelle }}</div>
                                <div class="pnr-search-item-price">{{ fmt(r.prix) }}</div>
                                <div v-if="r.emplacement" class="pnr-search-item-emp">
                                    <span v-if="r.emplacement.rayon">Rayon {{ r.emplacement.rayon }}</span>
                                    <span v-if="r.emplacement.couloir"> · Couloir {{ r.emplacement.couloir }}</span>
                                </div>
                            </div>
                            <div class="pnr-search-item-add">
                                <i class="ri-add-line"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Pavé numérique -->
            <div v-if="keypad.open" class="pnr-modal" @click.self="keypadAnnuler">
                <div class="pnr-keypad">
                    <div class="pnr-kp-head">
                        <span>{{ keypad.libelle }}</span>
                        <strong>{{ keypad.value || 0 }}</strong>
                    </div>
                    <div class="pnr-kp-grid">
                        <button v-for="n in [1,2,3,4,5,6,7,8,9]" :key="n" @click="keypadPress(n)">{{ n }}</button>
                        <button @click="keypadBackspace"><i class="ri-delete-back-2-line"></i></button>
                        <button @click="keypadPress(0)">0</button>
                        <button class="pnr-kp-ok" @click="keypadValider"><i class="ri-check-line"></i></button>
                    </div>
                </div>
            </div>

        </template>

        <!-- ============ VUE CODE CAISSE ============ -->
        <template v-else>
            <div class="pnr-code">
                <div class="pnr-code-scroll">

                    <!-- Badge succès -->
                    <div class="pnr-badge-wrap">
                        <div class="pnr-badge"><i class="ri-check-line"></i></div>
                        <div class="pnr-badge-lock"><i class="ri-lock-2-fill"></i></div>
                    </div>
                    <h2 class="pnr-code-title">Panier prêt</h2>
                    <p class="pnr-code-sub">Présentez ce code à la caisse</p>

                    <!-- Carte QR -->
                    <div class="pnr-qr-card">
                        <div class="pnr-qr-box">
                            <img v-if="caisseQr" :src="caisseQr" alt="Code caisse" />
                        </div>
                        <div class="pnr-code-ref">
                            <div class="pnr-code-ref-row">
                                <span class="pnr-code-value">{{ sessionId }}</span>
                                <button class="pnr-copy" @click="copierCode">
                                    <i :class="copied ? 'ri-check-line' : 'ri-file-copy-line'"></i>
                                    <span>{{ copied ? 'Copié' : 'Copier' }}</span>
                                </button>
                            </div>
                            <div class="pnr-code-ref-label">Référence de transaction</div>
                        </div>
                    </div>

                    <!-- Récap chips -->
                    <div class="pnr-chips">
                        <div class="pnr-chip"><i class="ri-shopping-basket-2-line"></i> {{ items.length }} article(s)</div>
                        <div class="pnr-chip primary"><i class="ri-money-dollar-circle-line"></i> Total indicatif : {{ fmt(total) }}</div>
                    </div>

                    <!-- Info -->
                    <div class="pnr-note">
                        <i class="ri-information-line"></i>
                        <p>Le caissier va scanner ce code, contrôler vos articles, puis procéder à l'encaissement.</p>
                    </div>

                </div>

                <!-- Actions -->
                <div class="pnr-code-actions">
                    <button class="pnr-act-primary" @click="nouveauPanier">
                        <i class="ri-add-circle-line"></i> Nouveau panier
                    </button>
                    <button class="pnr-act-secondary" @click="reprendre">
                        <i class="ri-arrow-go-back-line"></i> Reprendre le panier
                    </button>
                </div>
            </div>
        </template>

    </div>
</template>

<script>
    /* eslint-disable */
    import QRCode from "qrcode";
    // Même fonction que la caisse en mode supermarché (form_index6.vue:796) :
    // le prix affiché au client doit être celui qui lui sera facturé.
    import { getPUisupermacher } from "@/allfunction";
    import { imagesActives, reglerImages, urlPhoto } from "@/service/imagesArticles";
import {
    getOrCreateDeviceId,
    genSessionId,
    saveCart,
    getCart,
    clearCart,
    purgeOtherScopes,
    getArticleByEan,
    searchArticlesByName,
    getCatalogueMeta,
    touchCatalogueMeta,
    saveCatalogue,
    applyCatalogueDelta,
    verifyCatalogueIntegrity,
    clearCatalogue,
    saveSession,
    getSession,
    isSessionFresh,
    clearSession,
    verifierAccesScanGo,
} from "@/service/hsigscangoIndexdb";

    export default {
        name: "panierapp",
        data: () => ({
            // Images des articles : choix avec / sans propre à l'appareil ;
            // photosIllisibles = articles dont l'image n'a pas pu être chargée.
            avecImages: imagesActives(),
            photosIllisibles: [],
            items: [],
            sessionId: "",
            view: "scan",
            online: navigator.onLine,
            feedback: null,
            feedbackTimer: null,
            manualCode: "",
            keypad: { open: false, lineId: null, value: "", libelle: "" },
            reader: null,
            scannerControls: null,
            scanError: false,
            lastScan: { code: "", t: 0 },
            scanning: false,
            stream: null,
            track: null,
            detector: null,
            rafId: null,
            torchAvailable: false,
            torchOn: false,
            scanMode: "bouton",   // 'bouton' | 'continu'
            captureActive: false,
            captureTimer: null,
            lastAdded: null,      // { id, wasNew } pour Annuler
            caisseQr: "",
            copied: false,
            societeParam: "",
            magParam: "",
            keyParam: "",
            randParam: "",
            deviceId: "",
            cartRestored: false,   // tant que false, le watch n'écrit pas
            saveCartTimer: null,
            scanBusy: false,       // garde de réentrance du lookup asynchrone
            bootState: "checking", // 'checking' | 'ready' | 'offline' | 'denied'
            sessionMeta: null,
            catalogueMeta: null,
            seedProgress: null,    // { fait, total } pendant un gros téléchargement
            abortCtrl: null,
            demonte: false,        // garde les callbacks async après démontage
            refreshState: "idle",  // 'idle' | 'loading' | 'success' | 'error'
            lastSyncAt: 0,
            refreshResetTimer: null,
            showSearch: false,
            searchQuery: "",
            searchResults: [],
            searching: false,
            searchTimer: null,
        }),
        watch: {
            items: {
                handler() { this.scheduleSaveCart(); },
                deep: true,
            },
            view() { this.scheduleSaveCart(); },
        },
        computed: {
            total() {
                return this.items.reduce((s, l) => s + l.prix * l.qte, 0);
            },
            syncIcon() {
                return {
                    loading: "ri-loader-4-line pnr-spin",
                    success: "ri-check-line",
                    error: "ri-error-warning-line",
                }[this.refreshState] || "ri-refresh-line";
            },
            syncTitle() {
                return this.refreshState === "loading" ? "Synchronisation…" : "Mettre à jour le catalogue";
            },
            dateCatalogue() {
                const t = this.catalogueMeta && this.catalogueMeta.syncedAtServer;
                if (!t) return "-";
                try {
                    return new Date(t).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
                } catch (e) {
                    return "-";
                }
            },
            feedbackIcon() {
                if (!this.feedback) return "";
                return {
                    added: "ri-checkbox-circle-line",
                    dup: "ri-add-circle-line",
                    notfound: "ri-error-warning-line",
                    miss: "ri-search-eye-line",
                    scan: "ri-loader-4-line pnr-spin",
                }[this.feedback.type] || "ri-information-line";
            },
        },
        methods: {
            /* ------------------------------------------------ images */
            photoArticle(a) {
                if (!this.avecImages || !a || this.photosIllisibles.indexOf(a.ean) !== -1) return "";
                return urlPhoto(a.photo);
            },
            photoIllisible(a) { this.photosIllisibles = this.photosIllisibles.concat(a.ean); },
            basculerImages() { this.avecImages = reglerImages(!this.avecImages); },

            fmt(n) {
                return (Math.round(n) || 0).toLocaleString("fr-FR") + " F";
            },

            genSession() {
                this.sessionId = genSessionId(this.societeParam, this.magParam, this.deviceId);
            },

            /**
             * Écrit le panier au plus une fois par rafale. incr/decr et le pavé
             * numérique déclenchent des salves ; chaque écriture coûte un
             * AES.encrypt synchrone plus une transaction IDB.
             */
            scheduleSaveCart() {
                if (!this.cartRestored) return;   // ne pas écraser avant restauration
                if (this.saveCartTimer) clearTimeout(this.saveCartTimer);
                this.saveCartTimer = setTimeout(() => {
                    this.saveCartTimer = null;
                    this.flushSaveCart();
                }, 400);
            },

            cancelSaveCart() {
                if (this.saveCartTimer) {
                    clearTimeout(this.saveCartTimer);
                    this.saveCartTimer = null;
                }
            },

            async flushSaveCart() {
                this.cancelSaveCart();
                if (!this.cartRestored || !this.societeParam || !this.magParam) return;
                try {
                    await saveCart(this.societeParam, this.magParam, this.sessionId, {
                        items: JSON.parse(JSON.stringify(this.items)),
                        view: this.view,
                    });
                } catch (e) {
                    console.error("[sg] échec sauvegarde panier:", e);
                }
            },

            async restoreCart() {
                let cart = null;
                try {
                    cart = await getCart(this.societeParam, this.magParam);
                } catch (e) {
                    console.error("[sg] échec restauration panier:", e);
                }
                if (cart && cart.items.length) {
                    const { gardees, retirees } = await this.revaliderLignes(cart.items);
                    this.items = gardees;
                    this.signalerRetraits(retirees);
                    this.sessionId = cart.sessionId || genSessionId(this.societeParam, this.magParam, this.deviceId);
                    if (cart.view === "code" || cart.view === "scan") this.view = cart.view;
                } else {
                    this.genSession();
                }
                this.cartRestored = true;
            },

            /**
             * Confronte les lignes au catalogue courant : le panier peut dater
             * d'avant une synchro. Article disparu → ligne retirée ; prix modifié
             * → aligné silencieusement sur le prix courant.
             *
             * N'affiche rien elle-même : c'est à l'appelant de le faire, car un
             * retrait d'article est plus important à annoncer qu'un « catalogue
             * à jour » et ne doit pas se faire écraser par lui.
             */
            async revaliderLignes(lignes) {
                const gardees = [];
                let retirees = 0;
                for (const l of lignes) {
                    const prod = await getArticleByEan(this.societeParam, this.magParam, l.ean);
                    if (!prod) { retirees++; continue; }
                    // Le panier peut dater d'avant une synchro : on réaligne
                    // libellé et grille tarifaire sur le catalogue courant, puis
                    // on recalcule le prix pour la quantité restaurée.
                    const ligne = {
                        ...l,
                        libelle: prod.libelle,
                        pu: prod.pu, pv: prod.pv, pu7: prod.pu7,
                        photo: prod.photo || "",
                    };
                    this.majPrixLigne(ligne);
                    gardees.push(ligne);
                }
                return { gardees, retirees };
            },

            signalerRetraits(retirees) {
                if (retirees <= 0) return false;
                this.showFeedback(
                    "notfound",
                    retirees + " article(s) retiré(s)",
                    "Plus disponibles en magasin",
                    { duration: 5000 }
                );
                return true;
            },

            async handleScan(code, source) {
                if (this.view !== "scan") return;
                const c = String(code || "").trim();
                if (!c) return;
                // anti-doublon caméra uniquement (frames en continu). La saisie
                // manuelle et le re-scan volontaire ne sont jamais bloqués.
                const now = Date.now();
                if (source === "camera" && c === this.lastScan.code && now - this.lastScan.t < 1200) return;
                this.lastScan = { code: c, t: now };

                // Le lookup est asynchrone : sans cette garde, deux décodages
                // concurrents (mode continu, ~plusieurs images/s) pourraient
                // pousser deux lignes au lieu d'incrémenter la quantité.
                if (this.scanBusy) return;
                this.scanBusy = true;
                try {
                    const prod = await getArticleByEan(this.societeParam, this.magParam, c);
                    if (!prod) {
                        this.showFeedback("notfound", "Produit introuvable", "Code " + c + " · voir un agent");
                        this.playFeedback(false);
                        return;
                    }
                    const existing = this.items.find((l) => l.ean === prod.ean);
                    if (existing) {
                        existing.qte += 1;
                        this.majPrixLigne(existing);
                        this.lastAdded = { ean: prod.ean, wasNew: false };
                        this.showFeedback("dup", "Quantité +1", prod.libelle + " · ×" + existing.qte, { undoable: true, duration: 3200 });
                    } else {
                        const ligne = {
                            ean: prod.ean, libelle: prod.libelle,
                            pu: prod.pu, pv: prod.pv, pu7: prod.pu7,
                            qte: 1, prix: 0,
                            emplacement: prod.emplacement || null,
                            photo: prod.photo || "",
                        };
                        this.majPrixLigne(ligne);
                        this.items.push(ligne);
                        this.lastAdded = { ean: prod.ean, wasNew: true };
                        this.showFeedback("added", "Ajouté au panier", prod.libelle + " · " + this.fmt(ligne.prix), { undoable: true, duration: 3200 });
                    }
                    this.playFeedback(true);
                } finally {
                    this.scanBusy = false;
                }
            },

            async ajouterManuel() {
                if (!this.manualCode.trim()) return;
                const code = this.manualCode;
                this.manualCode = "";
                await this.handleScan(code, "manual");
            },

            showFeedback(type, title, sub, opts) {
                opts = opts || {};
                this.feedback = { type, title, sub, undoable: !!opts.undoable };
                if (this.feedbackTimer) clearTimeout(this.feedbackTimer);
                if (!opts.sticky) {
                    this.feedbackTimer = setTimeout(() => { this.feedback = null; }, opts.duration || 2200);
                }
            },

            setMode(m) {
                if (this.scanMode === m) return;
                this.scanMode = m;
                try { localStorage.setItem("panier_scanmode", m); } catch (e) { /* ignore */ }
                this.captureActive = false;
                if (this.captureTimer) { clearTimeout(this.captureTimer); this.captureTimer = null; }
                this.feedback = null;
            },

            armCapture() {
                if (this.scanMode !== "bouton" || this.captureActive) return;
                this.captureActive = true;
                this.showFeedback("scan", "Recherche…", "Visez le code-barres", { sticky: true });
                this.captureTimer = setTimeout(() => { this.endCapture(false); }, 2600);
            },

            endCapture(found) {
                this.captureActive = false;
                if (this.captureTimer) { clearTimeout(this.captureTimer); this.captureTimer = null; }
                if (!found) {
                    this.showFeedback("miss", "Aucun code détecté", "Rapprochez-vous et réessayez", { duration: 2200 });
                }
            },

            // Aiguille les décodages caméra selon le mode
            onDecoded(code) {
                if (this.scanMode === "bouton") {
                    if (!this.captureActive) return;   // en mode bouton, on n'agit que pendant une capture
                    this.handleScan(code, "button");
                    this.endCapture(true);
                } else {
                    this.handleScan(code, "camera");
                }
            },

            undoLast() {
                if (!this.lastAdded) { this.feedback = null; return; }
                const line = this.items.find((l) => l.ean === this.lastAdded.ean);
                if (line) {
                    if (this.lastAdded.wasNew || line.qte <= 1) this.removeLine(line);
                    else line.qte -= 1;
                }
                this.lastAdded = null;
                this.lastScan = { code: "", t: 0 };
                this.feedback = null;
            },

            playFeedback(ok) {
                try {
                    const ctx = new (window.AudioContext || window.webkitAudioContext)();
                    const o = ctx.createOscillator();
                    const g = ctx.createGain();
                    o.connect(g); g.connect(ctx.destination);
                    o.frequency.value = ok ? 880 : 200;
                    g.gain.value = 0.08;
                    o.start();
                    setTimeout(() => { o.stop(); ctx.close(); }, ok ? 110 : 240);
                } catch (e) { /* ignore */ }
                if (navigator.vibrate) navigator.vibrate(ok ? 60 : [50, 40, 50]);
            },

            /**
             * Recalcule le prix unitaire d'après la quantité.
             *
             * Indispensable : les paliers `pv` sont dégressifs (getPUisupermacher).
             * Figer le prix à l'ajout et multiplier donnerait un total faux dès
             * qu'un palier s'applique - et certains articles ont `pu = 0`, leur
             * prix ne venant QUE des paliers : ils s'afficheraient à 0 F.
             * À appeler après TOUTE modification de la quantité.
             */
            majPrixLigne(line) {
                line.prix = getPUisupermacher(line, line.qte);
            },

            incr(line) { line.qte += 1; this.majPrixLigne(line); },
            decr(line) {
                if (line.qte > 1) { line.qte -= 1; this.majPrixLigne(line); }
                else this.removeLine(line);
            },
            removeLine(line) {
                const i = this.items.findIndex((l) => l.ean === line.ean);
                if (i > -1) this.items.splice(i, 1);
            },

            openKeypad(line) {
                this.keypad = { open: true, lineId: line.ean, value: "", libelle: line.libelle };
            },
            keypadPress(n) {
                if (this.keypad.value.length >= 4) return;
                this.keypad.value = (this.keypad.value + String(n)).replace(/^0+/, "");
            },
            keypadBackspace() {
                this.keypad.value = this.keypad.value.slice(0, -1);
            },
            keypadValider() {
                const line = this.items.find((l) => l.ean === this.keypad.lineId);
                if (line) {
                    const v = parseInt(this.keypad.value, 10);
                    if (v > 0) { line.qte = v; this.majPrixLigne(line); }
                    else this.removeLine(line);
                }
                this.keypadAnnuler();
            },
            keypadAnnuler() {
                this.keypad = { open: false, lineId: null, value: "", libelle: "" };
            },

            async genererCodeCaisse() {
                if (this.items.length === 0) return;
                const payload = {
                  /*  s: this.sessionId,*/
                    soc: this.keyParam,
                    mag: this.magParam,
                    l: this.items.map((l) => [l.ean, l.qte]),
                };
                try {
                    this.caisseQr = await QRCode.toDataURL(JSON.stringify(payload), {
                        errorCorrectionLevel: "M",
                        margin: 2,
                        width: 460,
                        color: { dark: "#0b3d2e", light: "#ffffff" },
                    });
                } catch (e) {
                    console.error("QR caisse:", e);
                }
                this.view = "code";
                this.stopScanner();
                // Moment critique : le client est en caisse. On n'attend pas le debounce.
                await this.flushSaveCart();
            },

            reprendre() {
                this.view = "scan";
                this.$nextTick(() => this.startScanner());
            },

            copierCode() {
                try {
                    if (navigator.clipboard) navigator.clipboard.writeText(this.sessionId);
                } catch (e) { /* ignore */ }
                this.copied = true;
                setTimeout(() => { this.copied = false; }, 2000);
            },

            async nouveauPanier() {
                this.items = [];
                this.caisseQr = "";
                this.feedback = null;
                this.genSession();
                this.view = "scan";
                try {
                    // Le watch déclenché par items=[] est annulé : sans ça, il
                    // réécrirait un panier vide 400 ms après la purge.
                    await this.$nextTick();
                    this.cancelSaveCart();
                    await clearCart(this.societeParam, this.magParam);
                } catch (e) {
                    console.error("[sg] échec purge panier:", e);
                }
                this.$nextTick(() => this.startScanner());
            },

            async startScanner() {
                if (this.scanning) return;
                const video = document.getElementById("pnr-video");
                if (!video) return;
                try {
                    // Caméra arrière, haute résolution (code plus net → décodage rapide)
                    const stream = await navigator.mediaDevices.getUserMedia({
                        audio: false,
                        video: {
                            facingMode: { ideal: "environment" },
                            width: { ideal: 1280 },
                            height: { ideal: 720 },
                        },
                    });
                    this.stream = stream;
                    video.srcObject = stream;
                    await video.play();

                    const track = stream.getVideoTracks()[0];
                    this.track = track;
                    const caps = track.getCapabilities ? track.getCapabilities() : {};
                    this.torchAvailable = !!caps.torch;
                    // Autofocus continu si dispo
                    try {
                        if (caps.focusMode && caps.focusMode.indexOf("continuous") !== -1) {
                            await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
                        }
                    } catch (e) { /* ignore */ }

                    this.scanError = false;
                    this.scanning = true;

                    // Priorité au détecteur natif (Android Chrome) - bien plus rapide
                    if ("BarcodeDetector" in window) {
                        await this.startNativeDetector(video);
                    } else {
                        await this.startZxing(video);
                    }
                } catch (e) {
                    console.warn("Scanner non démarré:", e);
                    this.scanError = true;
                    this.scanning = false;
                }
            },

            async startNativeDetector(video) {
                try {
                    let formats = ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "itf", "qr_code"];
                    if (window.BarcodeDetector.getSupportedFormats) {
                        const supported = await window.BarcodeDetector.getSupportedFormats();
                        formats = formats.filter((f) => supported.indexOf(f) !== -1);
                    }
                    this.detector = new window.BarcodeDetector({ formats });
                    const loop = async () => {
                        if (!this.scanning) return;
                        if (this.scanMode === "continu" || this.captureActive) {
                            try {
                                const codes = await this.detector.detect(video);
                                if (codes && codes.length) this.onDecoded(codes[0].rawValue);
                            } catch (e) { /* frame sans code */ }
                        }
                        this.rafId = setTimeout(loop, 110);
                    };
                    loop();
                } catch (e) {
                    console.warn("BarcodeDetector KO, bascule ZXing:", e);
                    await this.startZxing(video);
                }
            },

            async startZxing(video) {
                const browserMod = await import("@zxing/browser");
                const libMod = await import("@zxing/library");
                const { BrowserMultiFormatReader } = browserMod;
                const { DecodeHintType, BarcodeFormat } = libMod;
                const hints = new Map();
                hints.set(DecodeHintType.POSSIBLE_FORMATS, [
                    BarcodeFormat.EAN_13, BarcodeFormat.EAN_8,
                    BarcodeFormat.UPC_A, BarcodeFormat.UPC_E,
                    BarcodeFormat.CODE_128, BarcodeFormat.CODE_39,
                    BarcodeFormat.ITF, BarcodeFormat.QR_CODE,
                ]);
                hints.set(DecodeHintType.TRY_HARDER, true);
                this.reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 100 });
                this.scannerControls = await this.reader.decodeFromStream(
                    this.stream, video,
                    (result) => { if (result) this.onDecoded(result.getText()); }
                );
            },

            async toggleTorch() {
                if (!this.track) return;
                try {
                    this.torchOn = !this.torchOn;
                    await this.track.applyConstraints({ advanced: [{ torch: this.torchOn }] });
                } catch (e) {
                    this.torchOn = false;
                }
            },

            stopScanner() {
                this.scanning = false;
                if (this.rafId) { clearTimeout(this.rafId); this.rafId = null; }
                try { if (this.scannerControls) this.scannerControls.stop(); } catch (e) { /* ignore */ }
                this.scannerControls = null;
                this.reader = null;
                this.detector = null;
                try { if (this.stream) this.stream.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
                this.stream = null;
                this.track = null;
                this.torchOn = false;
                this.torchAvailable = false;
            },

            setOnline() { this.online = navigator.onLine; },

            /* ------------------------------------------------ démarrage */

            /**
             * Redirige vers la page de refus. L'URL du panier (qui contient la
             * clé de licence) est déposée dans sessionStorage plutôt que passée
             * en query : elle ne doit jamais apparaître dans une URL autre que
             * celle du QR code.
             */
            /**
             * Coupe toute écriture du panier. Indispensable avant une purge ou
             * une redirection : sans ça, la sauvegarde différée - et surtout le
             * flush du beforeUnmount déclenché par la redirection - réécrivent
             * le panier juste après l'avoir effacé.
             */
            arreterPersistancePanier() {
                this.cancelSaveCart();
                this.cartRestored = false;
            },

            refuser(code) {
                // Un appel encore en vol au moment où l'on quitte la page ne doit
                // pas éjecter le client vers la page de refus depuis là où il se
                // trouve désormais.
                if (this.demonte) return;
                this.bootState = "denied";
                this.arreterPersistancePanier();
                try {
                    sessionStorage.setItem("sg_retry_url", this.$route.fullPath);
                } catch (e) { /* mode privé restrictif : on perd juste le Réessayer */ }
                this.$router.replace({ name: "panierrefus", query: { c: code } });
            },

            /**
             * Purge ciblée après un refus. Ce que l'on garde dépend de la nature
             * du refus : « votre lien est faux » (permanent) n'appelle pas le
             * même traitement que « revenez plus tard » (transitoire).
             */
            async purgerApresRefus(code) {
                const soc = this.societeParam, mag = this.magParam;
                this.arreterPersistancePanier();
                try {
                    await clearSession(soc, mag);
                    if (code === "KEY_INVALID" || code === "MAG_UNKNOWN") {
                        await clearCatalogue(soc, mag);
                        await clearCart(soc, mag);
                    }
                } catch (e) {
                    console.error("[sg] purge après refus:", e);
                }
            },

            /** Machine à états du démarrage. Voir le plan Scan & Go, §4. */
            async bootstrap() {
                // 0. La clé était jusqu'ici lue puis ignorée : plus maintenant.
                if (!this.societeParam || !this.magParam || !this.keyParam) {
                    return this.refuser("PARAMS_INVALID");
                }
 
                this.bootState = "checking";
                this.deviceId = await getOrCreateDeviceId();

                // 3. Cache valide → on démarre sans le moindre appel réseau.
                const session = await getSession(this.societeParam, this.magParam);
                if (session && isSessionFresh(session) && session.key === this.keyParam) {
                    const meta = await getCatalogueMeta(this.societeParam, this.magParam);
                    if (meta && await verifyCatalogueIntegrity(this.societeParam, this.magParam, meta)) {
                        this.sessionMeta = session;
                        this.catalogueMeta = meta;
                        await this.demarrerPanier("ready");
                        // Volontairement AUCUN appel de contrôle ici. Tant que la
                        // session est fraîche, on ne dérange pas le backend : une
                        // vérification à chaque ouverture coûtait un aller-retour
                        // pour, presque toujours, s'entendre répondre « rien n'a
                        // changé ».
                        //
                        // La révocation reste bornée par ttlSeconds (1 h), ce qui
                        // était DÉJÀ la garantie de conception. Les autres filets
                        // subsistent : visibilitychange (conditionné à une session
                        // périmée), le bouton refresh, et le mount d'après.
                        return;
                    }
                }

                // 4. Cache absent, expiré, ou QR remplacé → vérification serveur.
                await this.verifierEtCharger(false);
            },

            async verifierEtCharger(forcerFull) {
                const soc = this.societeParam, mag = this.magParam;
                const meta = forcerFull ? null : await getCatalogueMeta(soc, mag);

                if (this.abortCtrl) this.abortCtrl.abort();
                this.abortCtrl = new AbortController();

                const res = await verifierAccesScanGo({
                    soc, mag,
                    key: this.keyParam,
                    deviceId: this.deviceId,
                    catalogueVersion: meta ? meta.version : null,
                    signal: this.abortCtrl.signal,
                });

                if (res.status === "denied") {
                    await this.purgerApresRefus(res.code);
                    return this.refuser(res.code);
                }

                if (res.status === "network") {
                    // Une panne réseau ne refuse jamais l'accès si l'on a de quoi
                    // travailler hors ligne.
                    const dispo = await getCatalogueMeta(soc, mag);
                    if (dispo) {
                        this.catalogueMeta = dispo;
                        await this.demarrerPanier("offline");
                        return;
                    }
                    return this.refuser("OFFLINE_NO_CACHE");
                }

                const ok = await this.appliquerReponse(res.data, forcerFull);
                if (ok) await this.demarrerPanier("ready");
            },

            /** Retourne false si un re-fetch complet a été relancé. */
            async appliquerReponse(data, dejaForce) {
                const soc = this.societeParam, mag = this.magParam;
                const cat = data.catalogue || {};
                const meta = {
                    version: cat.version,
                    checksum: cat.checksum,
                    serverNow: data.serverNow,
                };

                if (cat.mode === "full") {
                    this.seedProgress = { fait: 0, total: (cat.upsert || []).length };
                    await saveCatalogue(soc, mag, cat.upsert || [], meta, (fait, total) => {
                        this.seedProgress = { fait, total };
                    });
                    this.seedProgress = null;
                } else if (cat.mode === "delta") {
                    await applyCatalogueDelta(soc, mag, cat, meta);
                } else {
                    // 'unchanged' : le serveur croit qu'on a déjà le catalogue.
                    const local = await getCatalogueMeta(soc, mag);
                    const sain = local && await verifyCatalogueIntegrity(soc, mag, local);
                    if (!sain && !dejaForce) {
                        // Un seul rattrapage, jamais de boucle.
                        await clearCatalogue(soc, mag);
                        await this.verifierEtCharger(true);
                        return false;
                    }
                    // Le serveur vient de certifier notre copie : elle repart pour
                    // un TTL complet. Sans ça, elle serait purgée à l'échéance du
                    // dernier `full`, même confirmée tous les jours.
                    if (sain) await touchCatalogueMeta(soc, mag, data.serverNow);
                }

                await saveSession(soc, mag, {
                    key: this.keyParam,
                    serverNow: data.serverNow,
                    ttlSeconds: data.ttlSeconds,
                    quota: data.quota,
                    magLabel: data.magLabel,
                    socLabel: data.socLabel,
                });
                this.sessionMeta = await getSession(soc, mag);
                this.catalogueMeta = await getCatalogueMeta(soc, mag);
                return true;
            },

            async demarrerPanier(etat) {
                // La purge vient APRÈS que l'accès est établi, jamais avant.
                // Placée au début de bootstrap(), elle s'exécutait sur la foi de
                // la seule URL : ouvrir /panier/X/Y/Z, même sans clé valide,
                // effaçait le catalogue ET LE PANIER EN COURS du magasin
                // légitime, avant de renvoyer KEY_INVALID. Ici, toutes les voies
                // qui y mènent ont déjà obtenu l'accès au scope courant.
                await purgeOtherScopes(this.societeParam, this.magParam);
                await this.restoreCart();
                this.bootState = etat;
                this.$nextTick(() => this.startScanner());
            },

            /* ---------------------------------------- synchro manuelle */

            setRefreshState(etat) {
                this.refreshState = etat;
                if (this.refreshResetTimer) clearTimeout(this.refreshResetTimer);
                if (etat === "success" || etat === "error") {
                    this.refreshResetTimer = setTimeout(() => { this.refreshState = "idle"; }, 1800);
                }
            },

            /**
             * Met à jour le catalogue à la demande. Une erreur réseau ne renvoie
             * jamais vers la page de refus : on le signale et on reste sur le
             * cache. Seul un refus explicite du serveur coupe la session.
             */
            async rafraichirCatalogue() {
                if (this.refreshState === "loading") return;      // une seule requête en vol

                const reste = 15000 - (Date.now() - this.lastSyncAt);
                if (this.lastSyncAt && reste > 0) {
                    this.showFeedback("miss", "Déjà synchronisé", "Réessayez dans " + Math.ceil(reste / 1000) + " s");
                    return;
                }

                this.setRefreshState("loading");
                try {
                    if (this.abortCtrl) this.abortCtrl.abort();
                    this.abortCtrl = new AbortController();

                    const meta = await getCatalogueMeta(this.societeParam, this.magParam);
                    const res = await verifierAccesScanGo({
                        soc: this.societeParam,
                        mag: this.magParam,
                        key: this.keyParam,
                        deviceId: this.deviceId,
                        catalogueVersion: meta ? meta.version : null,
                        signal: this.abortCtrl.signal,
                    });

                    if (res.status === "denied") {
                        await this.purgerApresRefus(res.code);
                        return this.refuser(res.code);
                    }

                    if (res.status === "network") {
                        this.setRefreshState("error");
                        this.showFeedback("miss", "Synchro impossible", "Vérifiez votre connexion");
                        return;
                    }

                    const inchange = res.data.catalogue && res.data.catalogue.mode === "unchanged";
                    const ok = await this.appliquerReponse(res.data, false);
                    if (!ok) return;

                    let retirees = 0;
                    if (this.items.length) {
                        const r = await this.revaliderLignes(this.items);
                        this.items = r.gardees;
                        retirees = r.retirees;
                    }
                    if (this.bootState === "offline") this.bootState = "ready";   // le réseau est revenu

                    this.setRefreshState("success");
                    // Un retrait d'article prime sur le « catalogue à jour » : le
                    // client doit savoir que son panier a changé.
                    if (!this.signalerRetraits(retirees)) {
                        const n = this.catalogueMeta ? this.catalogueMeta.count : 0;
                        this.showFeedback(
                            "added",
                            inchange ? "Déjà à jour" : "Catalogue à jour",
                            n + " article(s) · " + this.dateCatalogue
                        );
                    }
                } catch (e) {
                    console.error("[sg] rafraîchissement:", e);
                    this.setRefreshState("error");
                } finally {
                    this.lastSyncAt = Date.now();
                    if (this.refreshState === "loading") this.setRefreshState("idle");
                }
            },

            /* ---------------------------------------- recherche produit */

            toggleSearch() {
                this.showSearch = !this.showSearch;
                if (this.showSearch) {
                    this.searchQuery = "";
                    this.searchResults = [];
                    this.$nextTick(() => {
                        const el = this.$el.querySelector(".pnr-search-input");
                        if (el) el.focus();
                    });
                }
            },

            closeSearch() {
                this.showSearch = false;
                this.searchQuery = "";
                this.searchResults = [];
                if (this.searchTimer) { clearTimeout(this.searchTimer); this.searchTimer = null; }
            },

            async doSearch() {
                const q = this.searchQuery.trim();
                if (!q) { this.searchResults = []; return; }
                if (this.searchTimer) { clearTimeout(this.searchTimer); this.searchTimer = null; }
                this.searching = true;
                try {
                    this.searchResults = await searchArticlesByName(this.societeParam, this.magParam, q);
                } catch (e) {
                    console.error("[sg] erreur recherche:", e);
                    this.searchResults = [];
                } finally {
                    this.searching = false;
                }
            },

            onSearchInput() {
                if (this.searchTimer) clearTimeout(this.searchTimer);
                this.searchTimer = setTimeout(() => this.doSearch(), 300);


 
            },

            ajouterParRecherche(prod) {
                this.handleScan(prod.ean, "search");
                this.closeSearch();
            },

            /**
             * Au retour au premier plan : re-vérification si la session n'est
             * plus fraîche (pas à chaque bascule d'onglet), et surtout
             * redémarrage du scanner. iOS et Android coupent le MediaStream en
             * arrière-plan : au retour, la vidéo est figée sans aucune erreur.
             */
            async onVisibility() {
                if (document.visibilityState !== "visible") return;
                if (this.bootState === "denied" || this.bootState === "checking") return;

                if (!isSessionFresh(this.sessionMeta)) {
                    await this.rafraichirCatalogue();
                }
                if (this.view === "scan" && this.track && this.track.readyState === "ended") {
                    this.stopScanner();
                    this.$nextTick(() => this.startScanner());
                }
            },

        },
        async mounted() {

            const p = this.$route.params || {};
            //this.societeParam = p.soc   || "";
            this.societeParam = p.key   || "";
            this.magParam     = p.mag   || "";
            this.keyParam     = p.key   || "";
            this.randParam    = p.rand1 || "";   // facultatif → peut être undefined

            this.scanMode = localStorage.getItem("panier_scanmode") || "bouton";
            window.addEventListener("online", this.setOnline);
            window.addEventListener("offline", this.setOnline);
            document.addEventListener("visibilitychange", this.onVisibility);

            await this.bootstrap();
        },
        beforeUnmount() {
            // Posé en premier : les callbacks async encore en vol s'y réfèrent
            // pour ne pas naviguer ni muter l'état d'un composant démonté.
            this.demonte = true;
            window.removeEventListener("online", this.setOnline);
            window.removeEventListener("offline", this.setOnline);
            document.removeEventListener("visibilitychange", this.onVisibility);
            if (this.refreshResetTimer) clearTimeout(this.refreshResetTimer);
            if (this.abortCtrl) this.abortCtrl.abort();
            this.flushSaveCart();
            this.stopScanner();
        },
    }
</script>

<style scoped>
    .pnr-app {
        position: fixed; inset: 0; z-index: 9999;
        display: flex; flex-direction: column;
        background: #f4f6f5; color: #14321f;
        font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
        overflow: hidden;
    }
    /* Écran d'attente : tant que l'accès n'est pas vérifié, le scanner ne démarre pas. */
    .pnr-boot {
        flex: 1; display: flex; flex-direction: column;
        align-items: center; justify-content: center; gap: 16px;
        background: #0F6E56; color: #fff; text-align: center; padding: 24px;
    }
    .pnr-boot-logo { font-size: 46px; opacity: .9; }
    .pnr-boot-spin { font-size: 28px; }
    .pnr-boot-txt { margin: 0; font-size: 14px; line-height: 1.6; opacity: .92; }

    .pnr-offline {
        display: flex; align-items: center; gap: 8px;
        padding: 8px 12px; background: #fef3c7; color: #92400e;
        font-size: 12px; line-height: 1.4; flex-shrink: 0;
    }
    .pnr-offline i { font-size: 15px; flex-shrink: 0; }

    .pnr-header {
        display: flex; align-items: center; justify-content: space-between;
        padding: 12px 14px; background: #0F6E56; color: #fff; flex-shrink: 0;
    }
    .pnr-brand { display: flex; align-items: center; gap: 8px; font-weight: 600; font-size: 16px; }
    .pnr-head-right { display: flex; align-items: center; gap: 10px; }
    .pnr-net { font-size: 12px; display: flex; align-items: center; gap: 4px; padding: 3px 9px; border-radius: 20px; }
    .pnr-net.on { background: rgba(255,255,255,.18); }
    .pnr-net.off { background: #b45309; }
    .pnr-newbtn { background: rgba(255,255,255,.18); color: #fff; border: 0; width: 34px; height: 34px; border-radius: 50%; font-size: 16px; cursor: pointer; }
    .pnr-syncbtn { background: rgba(255,255,255,.18); color: #fff; border: 0; width: 34px; height: 34px; border-radius: 50%; font-size: 16px; cursor: pointer; }
    .pnr-syncbtn:disabled { cursor: default; opacity: .75; }
    .pnr-syncbtn.success { background: #16a34a; }
    .pnr-syncbtn.error { background: #b45309; }

    .pnr-scanzone { position: relative; background: #0c0c0c; flex-shrink: 0; overflow: hidden; }
    .pnr-reader { width: 100%; height: 240px; object-fit: cover; display: block; background: #0c0c0c; }
    .pnr-scanframe { position: absolute; top: 50%; left: 50%; width: 74%; height: 44%; transform: translate(-50%,-50%); border: 3px solid rgba(255,255,255,.85); border-radius: 12px; pointer-events: none; }
    .pnr-torch { position: absolute; top: 10px; right: 10px; width: 40px; height: 40px; border-radius: 50%; border: 0; background: rgba(0,0,0,.45); color: #fff; font-size: 20px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
    .pnr-torch.on { background: #f59e0b; color: #1a1a1a; }
    .pnr-scanhint { position: absolute; bottom: 8px; left: 0; right: 0; text-align: center; color: #fff; font-size: 12px; text-shadow: 0 1px 3px #000; pointer-events: none; }
    .pnr-scanerr { min-height: 120px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; color: #cbd5cf; font-size: 13px; text-align: center; padding: 14px; }
    .pnr-scanerr i { font-size: 30px; }

    .pnr-modebar { display: flex; justify-content: center; padding: 10px 12px 4px; background: #fff; flex-shrink: 0; }
    .pnr-seg { display: inline-flex; background: #eef1f0; border-radius: 999px; padding: 3px; }
    .pnr-seg button { border: 0; background: none; color: #5c6b64; font-size: 13px; font-weight: 600; padding: 7px 16px; border-radius: 999px; cursor: pointer; display: flex; align-items: center; gap: 5px; }
    .pnr-seg button.active { background: #0F6E56; color: #fff; }
    .pnr-shutter { flex-shrink: 0; margin: 8px 12px 0; height: 52px; border: 0; border-radius: 12px; background: #0F6E56; color: #fff; font-size: 16px; font-weight: 600; display: flex; align-items: center; justify-content: center; gap: 8px; cursor: pointer; box-shadow: 0 6px 16px rgba(15,110,86,.25); }
    .pnr-shutter:active { transform: scale(.98); }
    .pnr-shutter.busy { background: #6b7280; box-shadow: none; }
    .pnr-continu-hint { flex-shrink: 0; margin: 8px 12px 0; padding: 11px; text-align: center; font-size: 13px; color: #0F6E56; background: #e6f4ef; border-radius: 12px; display: flex; align-items: center; justify-content: center; gap: 6px; }
    .pnr-spin { animation: pnr-rotate 1s linear infinite; }
    @keyframes pnr-rotate { to { transform: rotate(360deg); } }

    .pnr-manual { display: flex; gap: 8px; padding: 10px 12px; background: #fff; border-bottom: 1px solid #e5e7eb; flex-shrink: 0; }
    .pnr-input { flex: 1; height: 40px; border: 1px solid #d1d5db; border-radius: 8px; padding: 0 12px; font-size: 15px; outline: none; }
    .pnr-input:focus { border-color: #0F6E56; }
    .pnr-addbtn { background: #0F6E56; color: #fff; border: 0; border-radius: 8px; padding: 0 16px; font-size: 14px; font-weight: 500; cursor: pointer; }

    .pnr-feedback { display: flex; align-items: center; gap: 10px; margin: 8px 12px 0; padding: 10px 12px; border-radius: 10px; flex-shrink: 0; }
    .pnr-feedback i { font-size: 22px; flex-shrink: 0; }
    .pnr-fb-text { flex: 1; min-width: 0; }
    .pnr-fb-title { font-weight: 600; font-size: 13px; }
    .pnr-fb-sub { font-size: 12px; opacity: .85; }
    .pnr-fb-undo { flex-shrink: 0; background: rgba(0,0,0,.08); border: 0; color: inherit; font-size: 12px; font-weight: 600; padding: 6px 12px; border-radius: 8px; cursor: pointer; display: flex; align-items: center; gap: 4px; }
    .pnr-fb-undo:active { transform: scale(.96); }
    .fb-added { background: #dcfce7; color: #0f6e56; }
    .fb-dup { background: #dbeafe; color: #1e40af; }
    .fb-notfound { background: #fee2e2; color: #b91c1c; }
    .fb-miss { background: #fef3c7; color: #92400e; }
    .fb-scan { background: #e5e7eb; color: #374151; }

    .pnr-list { flex: 1; overflow-y: auto; padding: 8px 12px 4px; }
    .pnr-empty { text-align: center; color: #9ca3af; padding: 36px 10px; }
    .pnr-empty i { font-size: 40px; }
    .pnr-empty p { font-size: 13px; margin-top: 8px; line-height: 1.5; }
    .pnr-line-img { width: 44px; height: 44px; flex-shrink: 0; object-fit: cover; border-radius: 8px; background: #f1f3f2; }
    .pnr-imgbtn { width: 34px; height: 34px; border-radius: 50%; border: 1px solid #dfe5e2; background: #fff; color: #8a9490; font-size: 16px; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
    .pnr-imgbtn.on { color: #0f6e56; border-color: #0f6e56; }
    .pnr-line { display: flex; align-items: center; gap: 8px; background: #fff; border: 1px solid #eef0ef; border-radius: 12px; padding: 10px; margin-bottom: 8px; }
    .pnr-line-info { flex: 1; min-width: 0; }
    .pnr-line-name { font-size: 13px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .pnr-line-pu { font-size: 11px; color: #9ca3af; margin-top: 2px; }
    .pnr-stepper { display: flex; align-items: center; gap: 8px; }
    .pnr-stepper button { width: 30px; height: 30px; border-radius: 50%; border: 1px solid #d1d5db; background: #fff; font-size: 15px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
    .pnr-qte { min-width: 30px; text-align: center; font-size: 16px; font-weight: 600; cursor: pointer; border-bottom: 2px solid #0F6E56; }
    .pnr-line-right { text-align: right; }
    .pnr-line-total { font-size: 13px; font-weight: 600; }
    .pnr-del { background: none; border: 0; color: #dc2626; font-size: 12px; cursor: pointer; margin-top: 2px; }

    .pnr-bottom { flex-shrink: 0; background: #fff; border-top: 1px solid #e5e7eb; padding: 10px 14px 14px; }
    .pnr-total { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 8px; }
    .pnr-total span { font-size: 13px; color: #6b7280; }
    .pnr-total strong { font-size: 20px; }
    .pnr-cta { width: 100%; background: #0F6E56; color: #fff; border: 0; border-radius: 10px; padding: 13px; font-size: 15px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; }
    .pnr-cta:disabled { background: #9ca3af; }
    .pnr-session { text-align: center; font-size: 11px; color: #9ca3af; margin-top: 8px; }

    .pnr-modal { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: flex-end; z-index: 10000; }
    .pnr-keypad { width: 100%; background: #fff; border-radius: 18px 18px 0 0; padding: 16px; }
    .pnr-kp-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 12px; }
    .pnr-kp-head span { font-size: 13px; color: #6b7280; }
    .pnr-kp-head strong { font-size: 26px; }
    .pnr-kp-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
    .pnr-kp-grid button { height: 52px; border: 1px solid #e5e7eb; background: #fff; border-radius: 10px; font-size: 20px; cursor: pointer; }
    .pnr-kp-ok { background: #0F6E56 !important; color: #fff; border-color: #0F6E56 !important; }

    .pnr-code { flex: 1; display: flex; flex-direction: column; background: #f9f9fb; overflow: hidden; }
    .pnr-code-scroll { flex: 1; overflow-y: auto; padding: 28px 20px 16px; display: flex; flex-direction: column; align-items: center; text-align: center; }
    .pnr-badge-wrap { position: relative; margin-bottom: 18px; }
    .pnr-badge { width: 88px; height: 88px; border-radius: 50%; background: #0F6E56; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 46px; box-shadow: 0 10px 24px rgba(15,110,86,.28); }
    .pnr-badge-lock { position: absolute; right: -4px; bottom: -4px; width: 32px; height: 32px; border-radius: 50%; background: #fff; color: #0F6E56; display: flex; align-items: center; justify-content: center; font-size: 17px; box-shadow: 0 2px 8px rgba(0,0,0,.15); }
    .pnr-code-title { font-size: 24px; font-weight: 700; color: #141b2b; margin: 0 0 4px; }
    .pnr-code-sub { font-size: 15px; color: #5c5f60; margin: 0 0 22px; }
    .pnr-qr-card { width: 100%; max-width: 340px; background: #fff; border-radius: 16px; box-shadow: 0 6px 20px rgba(0,0,0,.06); padding: 18px; }
    .pnr-qr-box { width: 100%; max-width: 260px; aspect-ratio: 1; margin: 0 auto; background: #fff; border: 1px solid #e6eae8; border-radius: 12px; padding: 12px; display: flex; align-items: center; justify-content: center; }
    .pnr-qr-box img { width: 100%; height: 100%; object-fit: contain; }
    .pnr-code-ref { margin-top: 16px; }
    .pnr-code-ref-row { display: flex; align-items: center; justify-content: center; gap: 10px; }
    .pnr-code-value { font-family: ui-monospace, "JetBrains Mono", monospace; font-size: 24px; font-weight: 700; letter-spacing: 2px; color: #005440; }
    .pnr-copy { display: inline-flex; align-items: center; gap: 5px; background: none; border: 0; color: #0F6E56; font-size: 13px; font-weight: 600; cursor: pointer; padding: 4px 8px; border-radius: 8px; }
    .pnr-copy:active { transform: scale(.96); }
    .pnr-code-ref-label { font-size: 11px; letter-spacing: .12em; text-transform: uppercase; color: #8a8f90; margin-top: 6px; }
    .pnr-chips { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; margin-top: 22px; }
    .pnr-chip { display: inline-flex; align-items: center; gap: 7px; padding: 9px 16px; border-radius: 999px; background: #e6eef0; color: #374151; font-size: 14px; font-weight: 600; }
    .pnr-chip.primary { background: #d7f2e8; color: #005440; }
    .pnr-note { display: flex; gap: 12px; align-items: flex-start; margin-top: 20px; padding: 14px; background: #fff; border: 1px solid #e9ecef; border-radius: 14px; max-width: 340px; text-align: left; }
    .pnr-note i { color: #0F6E56; font-size: 20px; flex-shrink: 0; }
    .pnr-note p { font-size: 13px; color: #5c6b64; margin: 0; line-height: 1.5; }
    .pnr-code-actions { flex-shrink: 0; background: rgba(255,255,255,.92); backdrop-filter: blur(8px); border-top: 1px solid #eceef0; padding: 12px 20px calc(14px + env(safe-area-inset-bottom)); display: flex; flex-direction: column; gap: 10px; }
    .pnr-act-primary { width: 100%; height: 54px; border: 0; border-radius: 14px; background: #0F6E56; color: #fff; font-size: 16px; font-weight: 600; display: flex; align-items: center; justify-content: center; gap: 8px; cursor: pointer; box-shadow: 0 6px 16px rgba(15,110,86,.25); }
    .pnr-act-primary:active { transform: scale(.98); }
    .pnr-act-secondary { width: 100%; height: 54px; border: 2px solid #cbd5d1; border-radius: 14px; background: #fff; color: #0F6E56; font-size: 16px; font-weight: 600; display: flex; align-items: center; justify-content: center; gap: 8px; cursor: pointer; }
    .pnr-act-secondary:active { transform: scale(.98); }

    .pnr-fade-enter-active, .pnr-fade-leave-active { transition: opacity .2s; }
    .pnr-fade-enter-from, .pnr-fade-leave-to { opacity: 0; }

    /* FAB */
    .pnr-fab {
        position: fixed; bottom: 120px; right: 16px; z-index: 999;
        width: 52px; height: 52px; border-radius: 50%;
        background: #0F6E56; color: #fff; border: 0;
        font-size: 22px; cursor: pointer;
        display: flex; align-items: center; justify-content: center;
        box-shadow: 0 4px 14px rgba(15,110,86,.35);
        transition: transform .15s, box-shadow .15s;
    }
    .pnr-fab:active { transform: scale(.93); box-shadow: 0 2px 8px rgba(15,110,86,.25); }

    /* Modal recherche */
    .pnr-search-modal {
        width: 100%; max-height: 70vh; background: #fff;
        border-radius: 18px 18px 0 0; padding: 0;
        display: flex; flex-direction: column; overflow: hidden;
    }
    .pnr-search-head {
        display: flex; align-items: center; gap: 10px;
        padding: 14px 14px 10px; border-bottom: 1px solid #eef0ef;
        flex-shrink: 0;
    }
    .pnr-search-head i { font-size: 18px; color: #9ca3af; }
    .pnr-search-input {
        flex: 1; height: 40px; border: 0; outline: none;
        font-size: 15px; background: none; color: #14321f;
    }
    .pnr-search-input::placeholder { color: #9ca3af; }
    .pnr-search-clear {
        background: none; border: 0; color: #9ca3af;
        font-size: 18px; cursor: pointer; padding: 4px;
        display: flex; align-items: center;
    }
    .pnr-search-status {
        display: flex; align-items: center; justify-content: center; gap: 8px;
        padding: 32px 16px; font-size: 13px; color: #9ca3af; flex-shrink: 0;
    }
    .pnr-search-results {
        flex: 1; overflow-y: auto; padding: 4px 0;
    }
    .pnr-search-item {
        display: flex; align-items: center; gap: 10px;
        padding: 12px 14px; cursor: pointer; transition: background .15s;
        border-bottom: 1px solid #f4f6f5;
    }
    .pnr-search-item:active { background: #ecfdf5; }
    .pnr-search-item-info { flex: 1; min-width: 0; }
    .pnr-search-item-name { font-size: 13px; font-weight: 600; color: #14321f; }
    .pnr-search-item-price { font-size: 12px; color: #0F6E56; font-weight: 600; margin-top: 1px; }
    .pnr-search-item-emp { font-size: 11px; color: #9ca3af; margin-top: 2px; display: flex; gap: 2px; }
    .pnr-search-item-add {
        width: 32px; height: 32px; border-radius: 50%;
        background: #ecfdf5; color: #0F6E56;
        display: flex; align-items: center; justify-content: center;
        font-size: 16px; flex-shrink: 0;
    }

    /* Emplacement dans la ligne panier */
    .pnr-line-emp {
        display: flex; align-items: center; gap: 3px;
        font-size: 11px; color: #9ca3af; margin-top: 3px;
    }
    .pnr-line-emp i { font-size: 11px; }
</style>
